/**
 * MyClips V3 — Feed Kategori Sözlüğü (Shared)
 *
 * Tek kaynak: feed çipleri (FeedTabShell), client filter
 * (FeedContainerV3.clipMatchesCategory), AI tahmini
 * (compose/pipeline/categoryDetector) ve composer UI
 * (UploadComposerV3) hepsi buradan okur.
 *
 * "Tümü" feed UI'da varsayılan filtresiz seçenektir; composer
 * "Tümü" sunmaz — yükleyici "Otomatik" + 7 spesifik kategoriden
 * seçer. categoryDetector "all"'u **sonuç olarak döndürmez**;
 * tahmin yapamazsa null döner.
 *
 * Yeni kategori eklerken:
 *   - id ASCII slug (Türkçe karakter yok); hashtag varyantları lowercase
 *     ve diakritiksiz olmalı.
 *   - tag listesinin son halini buradan okuyan tüm tüketiciler
 *     senkron alır; ek bir yere yazma yok.
 */

export const FEED_CATEGORIES = [
  // FAZ 27 (Audit-D) — "Keşfet" → "Tümü" yeniden adlandırıldı.
  // "Sana Özel" tab adıyla aynı sözcük olmasın diye; çip kullanıcıya
  // varsayılan/filtresiz seçeneği anlatır. Detector hâlâ "all" id'sini
  // başka bağlamda kullanmaz; sadece feed çip görünümü değişti.
  { id: "all",       label: "Tümü",         tags: null },
  { id: "haber",     label: "Haber",     tags: ["haber", "news", "gundem", "gündem", "haberler"] },
  { id: "eglence",   label: "Eğlence",   tags: ["eglence", "eğlence", "fun", "mizah", "eğlenceli", "viral"] },
  { id: "spor",      label: "Spor",      tags: ["spor", "sports", "futbol", "basketbol", "voleybol", "tenis"] },
  { id: "teknoloji", label: "Teknoloji", tags: ["teknoloji", "tech", "tekno", "technology", "yazılım", "yazilim", "ai", "yapayzeka"] },
  { id: "oyun",      label: "Oyun",      tags: ["oyun", "gaming", "game", "oyunlar", "gamer"] },
  { id: "muzik",     label: "Müzik",     tags: ["muzik", "müzik", "music", "şarkı", "sarki", "konser"] },
  { id: "yemek",     label: "Yemek",     tags: ["yemek", "food", "tarif", "mutfak", "lezzet"] },
  // FAZ 7 — 9 yeni niş kategori (auto_category VALID_CATEGORIES ile birebir)
  { id: "dans",      label: "Dans",      tags: ["dans", "dance", "koreografi", "challenge"] },
  { id: "komedi",    label: "Komedi",    tags: ["komedi", "comedy", "skec", "skeç", "standup", "stand-up"] },
  { id: "egitim",    label: "Eğitim",    tags: ["egitim", "eğitim", "education", "ders", "tutorial", "nasilyapilir"] },
  { id: "moda",      label: "Moda",      tags: ["moda", "fashion", "stil", "ootd", "kiyafet", "kıyafet", "alisveris"] },
  { id: "sanat",     label: "Sanat",     tags: ["sanat", "art", "resim", "elisleri", "elişleri", "yaratici"] },
  { id: "hayvanlar", label: "Hayvanlar", tags: ["hayvanlar", "pets", "kedi", "kopek", "köpek", "doga", "doğa", "wildlife"] },
  { id: "fitness",   label: "Fitness",   tags: ["fitness", "egzersiz", "spor salonu", "salonu", "kardio", "antrenman", "saglikliyasam"] },
  { id: "seyahat",   label: "Seyahat",   tags: ["seyahat", "travel", "gezi", "tatil", "vlog", "sehirturu", "şehirturu"] },
  { id: "yasam",     label: "Yaşam",     tags: ["yasam", "yaşam", "life", "lifestyle", "motivasyon", "iliskiler", "ilişkiler", "aile"] },
];

/* Tüm geçerli id'ler (ALL dahil) — input validation için. */
export const FEED_CATEGORY_IDS = FEED_CATEGORIES.map((c) => c.id);

/* Composer'da kullanıcının seçebileceği kategoriler (Tümü hariç).
   "Otomatik" yapay bir UI sentinel'i — gerçek kategori değil; composer
   bu seçim için detectCategoryFromText() çıktısını upload sırasında
   serialize eder. */
export const SELECTABLE_CATEGORIES = FEED_CATEGORIES.filter((c) => c.id !== "all");

/* id → Set<lowercase tag> haritası. clipMatchesCategory ve
   detectCategoryFromText O(1) lookup için kullanır. */
export const CATEGORY_TAG_SETS = (() => {
  const map = new Map();
  for (const c of FEED_CATEGORIES) {
    if (!c.tags) continue;
    map.set(c.id, new Set(c.tags.map((t) => String(t).toLowerCase())));
  }
  return map;
})();

/* Sözlük tarafında "all" haricindeki kategori id'leri. detector çıktısı
   bu kümeden bir id veya null. */
export const DETECTABLE_CATEGORY_IDS = FEED_CATEGORIES
  .filter((c) => c.id !== "all" && Array.isArray(c.tags) && c.tags.length > 0)
  .map((c) => c.id);

/* Bir kategori id'sinin metin etiketini döndürür (UI için).
   Bilinmeyen id → "Tümü" varsayılanı. */
// `x.id` string ile karşılaştırılıyor; bilinmeyen id zaten "Tümü"ye düşüyor.
export function getCategoryLabel(id: string): string {
  const c = FEED_CATEGORIES.find((x) => x.id === id);
  return c ? c.label : "Tümü";
}

/* Sprint 9 / H3 — Onboarding interests'ten hashtag set'ine expansion.
   users/{uid}.interests = ["muzik","spor",...] kategori id'leri tutar.
   Bu helper, mevcut FEED_CATEGORIES.tags map'inden hashtag listesi üretir;
   useFollowedTags ile birleştirilince computeTagMatchBoost kategoriye
   uyan klipleri otomatik boost eder. */
// ⚠️ Çağıranı KİLİTLİ dosyada (`FeedContainerV3.tsx:466`) — o yüzden
// parametre `unknown`: her çağrıyı kabul eder, kilitli dosyada tek satır
// değişiklik gerektirmez.
// ⚠️ `if (!Array.isArray(x))` deseni KULLANILMADI: `Array.isArray` imzası
// `x is any[]` olduğu için bir `unknown`ı sessizce `any[]` yapar ve gövdenin
// kalanı tipsiz kalırdı (#755'te ölçülen kör nokta). Onun yerine sonuç
// `unknown[]`e bağlanıyor — davranış birebir aynı, tipsizlik yok.
export function expandInterestsToTags(interestIds: unknown): string[] {
  const liste: unknown[] = Array.isArray(interestIds) ? interestIds : [];
  if (liste.length === 0) return [];
  const out = new Set<string>();
  for (const id of liste) {
    const cat = FEED_CATEGORIES.find((c) => c.id === id);
    if (!cat || !Array.isArray(cat.tags)) continue;
    for (const t of cat.tags) {
      if (typeof t === "string" && t) out.add(t.toLowerCase());
    }
  }
  return Array.from(out);
}

export default FEED_CATEGORIES;
